/** Preferred public origin for canonical URLs (always www in production). */
export const SITE_ORIGIN = (
    process.env.REACT_APP_PUBLIC_URL ||
    "https://www.puzzleinteract.com"
)
    .trim()
    .replace(/\/$/, "");

/** Static public pages — paths match public/sitemap.xml (no trailing slash). */
export const STATIC_PAGE_SEO = {
    "/": {
        title: "Puzzle Interact",
        description:
            "Original crossword, word search, hidden message, sliding, and jigsaw puzzles with illustrated stories. Play in the browser; apps for iPhone and Android.",
    },
    "/about": {
        title: "About Us | Puzzle Interact",
        description:
            "Puzzle Interact (Jeux Puzzle Interact) in Quebec writes original illustrated stories and puzzles for web, iPhone, and Android.",
    },
    "/stories": {
        title: "Original Stories | Puzzle Interact",
        description:
            "Read original illustrated stories from Puzzle Interact, then play the matching crossword, word search, or hidden-message puzzle.",
    },
    "/storybook": {
        title: "Storybook | Puzzle Interact",
        description:
            "Browse the Puzzle Interact storybook: original illustrated tales with linked puzzles you can play in the browser.",
    },
    "/word-of-day": {
        title: "Word of the Day | Puzzle Interact",
        description:
            "A new word each day with meaning, an illustrated story, and a linked puzzle from Puzzle Interact.",
    },
    "/contact": {
        title: "Contact Us | Puzzle Interact",
        description:
            "Contact Puzzle Interact for support, privacy questions, or feedback.",
    },
    "/tutors": {
        title: "Tutors | Puzzle Interact",
        description:
            "English story puzzles, vocabulary, and printable worksheets for tutors. Tutor Standard from $4.99/month.",
    },
    "/tutors/library": {
        title: "Story Library | Puzzle Interact Tutors",
        description:
            "Save private tutoring stories and draft crosswords. Stories stay in your library and are not on the public puzzle lists.",
    },
    "/tutors/worksheets": {
        title: "Worksheet Generator | Puzzle Interact Tutors",
        description:
            "Print a two-page crossword worksheet: the empty grid and clues, then the answers and original lesson text.",
    },
    "/tutors/settings": {
        title: "Tutor Settings | Puzzle Interact",
        description:
            "Tutor Standard subscription, organization name and logo for worksheet headers, and extra PDF export credits.",
    },
    "/for-educators": {
        title: "Tutors | Puzzle Interact",
        description:
            "English story puzzles, vocabulary, and printable worksheets for tutors. Tutor Standard from $4.99/month.",
    },
};

/** Indexable `/puzzles/*` browse listings — paths match public/sitemap.xml (no trailing slash). */
export const PUZZLE_LISTING_SEO = {
    "/puzzles/crossword": {
        title: "Crossword Puzzles | Puzzle Interact",
        description:
            "Original illustrated-story crossword puzzles. Browse by topic and play free in the browser.",
    },
    "/puzzles/word-search": {
        title: "Word Search Puzzles | Puzzle Interact",
        description:
            "Story-based word search puzzles with themed word lists. Play free online on Puzzle Interact.",
    },
    "/puzzles/hidden-message": {
        title: "Hidden Message Puzzles | Puzzle Interact",
        description:
            "Find the word list, then read the leftover quote. Original hidden-message puzzles from Puzzle Interact.",
    },
    "/puzzles/sliding": {
        title: "Sliding Puzzles | Puzzle Interact",
        description:
            "Sliding tile puzzles from original story photos. Choose a story, then a grid size and difficulty.",
    },
    "/puzzles/jigsaw": {
        title: "Jigsaw Puzzles | Puzzle Interact",
        description:
            "Original illustrated jigsaw puzzles with optional color baskets. Browse stories and play in the browser.",
    },
    "/puzzles/jigsaw/packs": {
        title: "Jigsaw Premium Packs | Puzzle Interact",
        description:
            "Buy Jigsaw Muse picture packs on the web. Each pack stays unlocked on your account.",
    },
};

/** Strip trailing slashes except site root. */
export function normalizePathname(pathname) {
    if (!pathname || pathname === "/") {
        return "/";
    }
    return pathname.replace(/\/+$/, "") || "/";
}

export function getPuzzleListingSeo(pathname) {
    const path = normalizePathname(pathname);
    const wod = path.match(/^\/word-of-day\/(\d{4}-\d{2}-\d{2})$/);
    if (wod) {
        return {
            title: `Word of the Day (${wod[1]}) | Puzzle Interact`,
            description: `Original word of the day for ${wod[1]}: meaning, story, and a linked puzzle from Puzzle Interact.`,
        };
    }
    return PUZZLE_LISTING_SEO[path] || STATIC_PAGE_SEO[path] || null;
}

function isIndexablePlayBoardPath(pathname) {
    return (
        /^\/puzzle\/play-jigsaw\/[^/]+\/board\/[^/]+\/?$/.test(pathname) ||
        /^\/puzzle\/play-sliding\/[^/]+\/board\/[^/]+\/?$/.test(pathname)
    );
}

const NOINDEX_PATH_PREFIXES = [
    "/puzzle/play-",
    "/story/play",
    "/share/",
    "/admin",
    "/account",
    "/ad/",
    "/mobile-sliding",
    "/creator",
    "/login",
    "/dashboard",
    "/prototype",
    "/referral/",
    "/redirect/",
    "/subscription/success",
    "/verified",
    "/email/",
    "/deletion-status",
    "/data-deletion/request",
    "/data-deletion/verified",
    "/contact/detail/",
    "/error",
    "/notfound",
    "/unauthorized",
];

/**
 * Interactive / authenticated surfaces — crawlable for link discovery but not search snippets.
 * Keep in sync with production robots.txt Disallow rules (backend).
 */
export function shouldNoindexPath(pathname) {
    if (isIndexablePlayBoardPath(pathname)) {
        return false;
    }
    if (NOINDEX_PATH_PREFIXES.some(
        (prefix) => pathname === prefix || pathname.startsWith(prefix),
    )) {
        return true;
    }
    // Daily hub is indexable; per-story deep links are not.
    return /^\/puzzle-of-day\/.+/.test(pathname);
}

/**
 * Prefer a single crossword play URL when both `/puzzle/play-cross/:id` and `/story/play-cross/:id` exist.
 */
export function getCanonicalPathname(pathname) {
    const puzzleCross = pathname.match(/^\/puzzle\/play-cross\/([^/]+)\/?$/);
    if (puzzleCross) {
        return normalizePathname(`/story/play-cross/${puzzleCross[1]}`);
    }
    return normalizePathname(pathname);
}

export function buildCanonicalUrl(pathname, search = "") {
    const canonicalPath = getCanonicalPathname(pathname);
    const path = canonicalPath.startsWith("/") ? canonicalPath : `/${canonicalPath}`;
    void search;
    return `${SITE_ORIGIN}${path}`;
}

/**
 * Distinct language URLs only. Pointing en and fr at the same href is invalid
 * hreflang and makes Google treat story pages as duplicates of each other.
 */
export function buildHreflangAlternates(pathname, search = "") {
    void pathname;
    void search;
    return [];
}

export function getRobotsContent(pathname) {
    return shouldNoindexPath(pathname) ? "noindex, follow" : "index, follow";
}
